import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom'
import logo from '../../../assets/images/logo.svg'
import menuIcon from '../../../assets/images/menu-icon.svg'
import sidebarItems from './const'
import IFrame from '@uiw/react-iframe'
import { getAccount } from '../../../libs/services'
import { SideBarItem } from '../../../models/global.types'
import { setInstitution } from '../../../redux/slices/institutionSlice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../redux/store'
import { ReturnButton } from '../../../components/molecules'
import { useKeyboardControls } from '../../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../../enums/KeyboardControls'
import { KeyboardControl, ControlHighlight } from '../../../components/molecules/KeyboardControls'

export enum FocusableElement {
  ImageLogo = 'image-logo',
  ButtonAccountDetails = 'button-account-details',
  IframeExternalContent = 'iframe-external-content',
}

const Dashboard: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { pageType } = useParams()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const [contentUrl, setContentUrl] = useState<string>('')
  const [sidebarLinks, setSidebarLinks] = useState<SideBarItem[]>([])
  const CAMPUS_SLUG = 'your-campus-policies'
  const { institution } = useSelector((state: RootState) => state.institution)
  const keyboardControls = useKeyboardControls()

  useEffect(() => {
    if (pageType !== '' && !pathname.includes('account-details')) {
      navigate('/user-dashboard/setting/account-details')
    }

    if (institution === null) {
      getAccount()
        .then((res): void => {
          dispatch(setInstitution(res.data.account))
        })
        .catch(err => console.error(err))
    }
  }, [dispatch, institution, navigate, pageType, pathname])

  const fetchContent = (sidebarItem: SideBarItem): void => {
    setContentUrl(
      sidebarItem.slot === CAMPUS_SLUG
        ? institution?.campusPolicyUrl !== undefined ? institution.campusPolicyUrl : ''
        : sidebarItem.webPageUrl
    )
  }

  useEffect(() => {
    if (institution !== null) {
      let filteredLinks = []
      if (institution.campusPolicyUrl === undefined) {
        filteredLinks = sidebarItems.filter(item => item.slot !== CAMPUS_SLUG)
      } else {
        filteredLinks = sidebarItems
      }

      setSidebarLinks(filteredLinks)
    }
  }, [institution])

  const renderExternalContent = (): JSX.Element => {
    if (contentUrl === '') {
      return (
        <Outlet />
      )
    }

    return (
      <KeyboardControl isExternal>
        <IFrame
          id={FocusableElement.IframeExternalContent}
          src={contentUrl}
          onLoad={() => keyboardControls.setFocusedElement(FocusableElement.IframeExternalContent)}
          style={{ width: '100%', height: '100%' }}
        />
      </KeyboardControl>
    )
  }

  return (
    <div className='font-primary flex bg-[#030303] min-h-screen p-8'>
      <div className='flex flex-col glow-effect'>
        <div className='flex flex-col h-full relative min-w-[325px] shadow-[0px_0px_20px_-4px_rgba(52,232,158,1)] userdashboard-corner bg-dopage-lightGreen p-[5px]'>
          <div className='inside bg-neutral-2c h-full w-full flex flex-col relative min-w-[325px]'>
            <header className='flex justify-center mt-[38px]'>
              <img
                id={FocusableElement.ImageLogo}
                src={logo}
                className='w-[138px] cursor-pointer'
                alt='logo'
              />
            </header>
            <div className='px-[24px] mt-[64px]'>
              <ReturnButton id='return-button-settings' onClick={() => navigate('/user-dashboard')} text='Return to Dashboard' />
            </div>
            <ul className='px-[24px] mt-[96px] flex-1 flex flex-col'>
              {sidebarLinks.map((link, key) => {
                const id = `side-bar-menu-${key}`
                return (
                  <ControlHighlight
                    active={keyboardControls.isEnabled && keyboardControls.isElementFocused(id)}
                    highlightEffect={HighlightEffect.ButtonHexOutline}
                  >
                    <KeyboardControl>
                      <button
                        id={id}
                        key={id}
                        className={`flex cursor-pointer items-center text-[18px] rounded text-dopage-lightGreen font-bold py-[14px] px-[17px] hover:bg-[rgba(52,232,158,0.2)] ${(pageType === link.slot) ? 'bg-[rgba(52,232,158,0.2)]' : ''}`}
                        onClick={() => fetchContent(link)}
                      >
                        <img src={link.icon} className='w-[27px]' alt={`icon-${key}`} />
                        <span className='ml-4'>{link.title}</span>
                      </button>
                    </KeyboardControl>
                  </ControlHighlight>
                )
              })}
            </ul>
            <div className='px-[24px] pb-[50px]'>
              <ControlHighlight
                active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonAccountDetails)}
                highlightEffect={HighlightEffect.ButtonHexOutline}
                data-testid={`highlight-${FocusableElement.ButtonAccountDetails}`}
              >
                <KeyboardControl>
                  <button
                    id={FocusableElement.ButtonAccountDetails}
                    data-testid={FocusableElement.ButtonAccountDetails}
                    className={`px-[17px] flex cursor-pointer items-center text-[18px] rounded text-dopage-lightGreen font-bold py-[14px] hover:bg-[rgba(52,232,158,0.2)] ${(pathname === '/user-dashboard/setting/account-details') ? 'bg-[rgba(52,232,158,0.2)]' : ''}`}
                    onClick={
                      () => {
                        navigate('account-details')
                        setContentUrl('')
                      }
                    }
                  >
                    <img src={menuIcon} className='w-[27px]' alt='Account Details Icon' />
                    <span className='ml-4'>Account details</span>
                  </button>
                </KeyboardControl>
              </ControlHighlight>
            </div>
          </div>
        </div>
      </div>
      <div className='flex flex-col w-full'>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.IframeExternalContent)}
          highlightEffect={HighlightEffect.Iframe}
          className='ml-5 relative h-full shadow-[0px_0px_20px_-4px_rgba(52,232,158,1)] bg-dopage-lightGreen p-[5px] userdashboard-corner'
          activeClassName='!bg-dopage-accessibilityHighlight'
        >
          <div className='inside bg-neutral-2c h-full w-full'>
            {renderExternalContent()}
          </div>
        </ControlHighlight>
      </div>
    </div>
  )
}

export default Dashboard
